import { useCallback, useEffect } from 'react';

import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

interface UseRealTimeMessagingProps {
    eventName: RealTimeMessagingEvents;
    eventHandler?: (data: any) => void;
    identifier?: string;
}

const useRealTimeMessaging = ({ eventName, eventHandler, identifier }: UseRealTimeMessagingProps) => {

    const angularJSInjector = getInjector();
    const $rootScope = getRootScope(angularJSInjector);
    const $channelService = angularJSInjector.get('ChannelService');

    const getSubscribedUsers = useCallback(async () => {
        return await $channelService.getRegistered(eventName, identifier) as number[];
    }, [eventName, identifier]);

    const subscribe = useCallback(async (listen?: boolean) => {
        $channelService.register(eventName, identifier, listen);
    }, [eventName, identifier]);

    const unsubscribe = useCallback(async (listen?: boolean) => {
        $channelService.unregister(eventName, identifier, listen);
    }, [eventName, identifier]);

    const broadcast = useCallback(async (data: any) => {
        $channelService.broadcast(eventName, identifier, data);
    }, [eventName, identifier]);

    const isRegistered = useCallback(async (data: any) => {
        $channelService.isRegistered(eventName, identifier, data);
    }, [eventName, identifier]);

    useEffect(() => {
        const deregisterEvent = $rootScope?.$on('channelMessageReceived', (_: string, eventMessage: any) => {
            if (eventMessage.event === eventName) {
                eventHandler && eventHandler(eventMessage.data);
            }
        });

        return () => ( deregisterEvent ) ? deregisterEvent() : {};

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventName, eventHandler]);

    return {
        broadcast,
        getSubscribedUsers,
        subscribe,
        unsubscribe,
        isRegistered
    };
};

export default useRealTimeMessaging;
